import {datetimeFormatter} from '@hconnect/uikit'
import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {Icon, LabelRegular, Table, TextRegular, useSortableTable} from '@hm/fundamental'
import {mdiPlus} from '@mdi/js'
import {Box} from '@mui/material'
import moment from 'moment'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {useLayoutStyles} from '../styles'
import {JobsToBeDone, JobsToBeDoneTaskWithContext, ViewMode} from '../types'
import {flattenTasksWithContext} from '../utils'

interface Props {
  timezone: string
  selectedItem: JobsToBeDoneTaskWithContext | undefined
  setSelectedItem: (item: JobsToBeDoneTaskWithContext) => void
  jobsToBeDone: JobsToBeDone
  isEditAllowed: boolean
  setViewMode: (mode: ViewMode) => void
}

export const JobsToBeDoneTable: React.FC<Props> = ({
  jobsToBeDone,
  selectedItem,
  setSelectedItem,
  isEditAllowed,
  timezone,
  setViewMode
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const tasks = useMemo(
    () =>
      flattenTasksWithContext([
        {
          ...jobsToBeDone,
          activities: jobsToBeDone.activities
        }
      ]),
    [jobsToBeDone]
  )

  const onCrossClick = (item: JobsToBeDoneTaskWithContext) => {
    setSelectedItem(item)
  }

  const onRowClick = (item: JobsToBeDoneTaskWithContext) => {
    setSelectedItem(item)
    setViewMode(ViewMode.detailsMode)
  }

  const {classes} = useLayoutStyles()
  const config = useSortableTable<JobsToBeDoneTaskWithContext>(
    [
      {
        id: 'activityTitle',
        label: t('jobsToBeDone.table.activity'),
        valueClassName: classes.tableCell1
      },
      {
        id: 'title',
        label: t('jobsToBeDone.table.task'),
        valueClassName: classes.tableCell2
      },
      {
        id: 'frequency',
        label: t('jobsToBeDone.table.frequency'),
        valueClassName: classes.tableCell3
      },
      {
        label: t('jobsToBeDone.table.latestInsight'),
        valueClassName: classes.tableCell4,
        render: (data) => {
          if (data.latestInsightAt && data.latestInsightAuthor) {
            return (
              <div className="flex flex-col">
                <TextRegular>
                  {datetimeFormatter(moment(data.latestInsightAt), timezone)}
                </TextRegular>
                <LabelRegular className={'text-foreground_faded'}>{`${t('jobsToBeDone.table.by')} ${
                  data.latestInsightAuthor.name
                }`}</LabelRegular>
              </div>
            )
          } else {
            return <TextRegular>-</TextRegular>
          }
        }
      },
      {
        id: 'id',
        label: '', // empty column for the add icon
        render: (data) =>
          isEditAllowed ? (
            <div className={classes.cursorPointer}>
              <button
                data-test-id="add-insight-icon"
                onClick={(e) => {
                  e.stopPropagation()
                  onCrossClick(data)
                }}
              >
                <Icon
                  path={mdiPlus}
                  size="regular"
                  color={customThemeConstants().palette.barChartDefault}
                />
              </button>
            </div>
          ) : null
      }
    ],
    [selectedItem?.id],
    {
      prefix: 'test-row',
      postfixFieldName: 'id'
    },
    {enableLocalSorting: true}
  )

  return (
    <Box
      sx={{
        '& tbody tr': {
          transition: 'background-color 0.2s'
        },
        ...(selectedItem?.id && {
          [`& tbody tr[data-row-id="row-${selectedItem.id}"]`]: {
            backgroundColor: customThemeConstants().palette.contentSelected
          }
        })
      }}
    >
      <Table
        config={config}
        getRowId={(row) => `row-${row.id}`}
        data={tasks}
        breakpoint="lg"
        onRowClick={(row) => onRowClick(row)}
      />
    </Box>
  )
}
